/**
 * Sign-in enrolment: the company requires two-step verification and this
 * account has not set it up yet, so `wageproAuth.login` returned a
 * `setup_token` (+ its `binding_secret`) instead of a session. Steps:
 * fetch enrolment material → scan / type the key → confirm a code →
 * save recovery codes → enter the app.
 *
 * The session already exists by the time recovery codes show
 * (`completeEnrolment` issued it); entering the app waits for the user to
 * confirm they saved the codes, because this is their only chance to.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, FormField, Input, Skeleton } from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import { nativeClientInput } from '../../lib/auth/native_shell_auth.js';
import type { NativeSessionCarrier } from '../../lib/auth/native_shell_auth.js';
import {
  classifyTwoFactorError,
  isAuthenticatorCode,
  normaliseTwoFactorCode,
  type TwoFactorErrorKind,
} from '../../lib/auth/two_factor_logic.js';
import { AuthenticatorSetupCard } from '../../components/auth/authenticator_setup_card.js';
import { RecoveryCodesPanel } from '../../components/auth/recovery_codes_panel.js';
import { TwoFactorError } from '../../components/auth/two_factor_error.js';
import { finishSignIn } from './finish_sign_in.js';

type Step =
  | { kind: 'loading' }
  | { kind: 'scan'; secret: string; qrSvg: string }
  | { kind: 'codes'; codes: string[]; session: NativeSessionCarrier };

export function TwoFactorSetupStep({
  setupToken,
  bindingSecret,
  onSignedIn,
  onStartOver,
}: {
  setupToken: string;
  bindingSecret: string;
  onSignedIn?: () => void;
  onStartOver: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<TwoFactorErrorKind | null>(null);

  // `setupEnrolment` WRITES a fresh secret, so it must run exactly once per
  // token: a second call (React StrictMode re-running effects in dev) would
  // replace the secret behind the QR code already on screen.
  const requestedFor = useRef<string | null>(null);
  useEffect(() => {
    if (requestedFor.current === setupToken) return;
    requestedFor.current = setupToken;
    getTrpcClient()
      .wageproTwoFactor.setupEnrolment.mutate({ setup_token: setupToken })
      .then((material) => {
        setStep({
          kind: 'scan',
          secret: material.secret,
          qrSvg: material.qr_svg,
        });
      })
      .catch((err: unknown) => {
        setError(classifyTwoFactorError(err));
      });
  }, [setupToken]);

  async function handleConfirm(): Promise<void> {
    const normalised = normaliseTwoFactorCode(code);
    if (!isAuthenticatorCode(normalised)) {
      setError('invalid_code');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result =
        await getTrpcClient().wageproTwoFactor.completeEnrolment.mutate({
          setup_token: setupToken,
          binding_secret: bindingSecret,
          code: normalised,
          ...nativeClientInput(isNative()),
        });
      setStep({ kind: 'codes', codes: result.backup_codes, session: result });
    } catch (err) {
      setError(classifyTwoFactorError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEnterApp(session: NativeSessionCarrier): Promise<void> {
    const outcome = await finishSignIn(session, onSignedIn);
    if (outcome === 'session_not_stored') setError('unknown');
  }

  if (step.kind === 'codes') {
    return (
      <div className="flex flex-col gap-3">
        <RecoveryCodesPanel
          codes={step.codes}
          doneLabel={t('two_factor.continue_to_wagepro')}
          onDone={() => void handleEnterApp(step.session)}
        />
        {error !== null && <TwoFactorError kind={error} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-[color:var(--ink)]">
          {t('two_factor.setup_required_title')}
        </h1>
        <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
          {t('two_factor.setup_required_dek')}
        </p>
      </div>

      {step.kind === 'loading' ? (
        error === null ? (
          <Skeleton className="nv-skel h-40 w-full" />
        ) : null
      ) : (
        <>
          <AuthenticatorSetupCard qrSvg={step.qrSvg} secret={step.secret} />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <FormField
              label={t('two_factor.code_label')}
              htmlFor="two-factor-setup-code"
            >
              <Input
                id="two-factor-setup-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                autoComplete="one-time-code"
                inputMode="numeric"
                disabled={submitting}
                className="mono"
              />
            </FormField>
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              className="w-full justify-center"
            >
              {submitting ? t('two_factor.verifying') : t('two_factor.turn_on')}
            </Button>
          </form>
        </>
      )}

      {error !== null && <TwoFactorError kind={error} />}

      <Button
        type="button"
        variant="ghost"
        onClick={onStartOver}
        disabled={submitting}
      >
        {t('two_factor.back_to_sign_in')}
      </Button>
    </div>
  );
}
